"use client"

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronLeft, Circle } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"
import { font_title_secularOne } from "../fonts"

const BG_COLOR = "bg-nfPurple-950/80"
const TEXT_COLOR = "white"
const BORDER_COLOR = "border-nfPurple-700"
const SEPARATOR_COLOR = "bg-nfPurple-600"

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ ref }
    className={ cn(
      `flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-base outline-none focus:bg-white/30  data-[state=open]:bg-nfBlue-700/10`,
      inset && "pr-8",
      className
    ) }
    { ...props }
  >
    { children }
    <ChevronLeft className="mr-auto h-5 w-5" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ ref }
    className={ cn(
      `z-50 min-w-[10rem] text-center overflow-hidden rounded-md border ${BORDER_COLOR} ${BG_COLOR} p-2 ${TEXT_COLOR} shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
      className
    ) }
    { ...props }
  />
))
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ ref }
      sideOffset={ sideOffset }
      className={ cn(
        `z-20 relative min-w-[10rem] gap-1 overflow-hidden rounded-md border ${BORDER_COLOR} ${BG_COLOR} backdrop-blur-sm p-2 ${TEXT_COLOR} shadow-xl shadows-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
        className
      ) }
      { ...props }
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ ref }
    dir="rtl"
    className={ cn(
      `relative flex cursor-pointer justify-center select-none items-center rounded-sm px-3 py-2 text-base outline-none transition-colors bg-white/5 focus:bg-white/30  focus:${TEXT_COLOR} data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      inset && "pr-8",
      className
    ) }
    { ...props }
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ ref }
    className={ cn(
      `relative flex cursor-pointer select-none items-center rounded-sm py-2 pr-3 pl-8 text-base outline-none transition-colors focus:bg-white/30  focus:${TEXT_COLOR} data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      className
    ) }
    checked={ checked }
    { ...props }
  >
    <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-5 w-5" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    { children }
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ ref }
    className={ cn(
      `relative flex select-none items-center rounded-sm py-2 pr-3 pl-8 text-base outline-none transition-colors focus:bg-white/30 cursor-pointer focus:${TEXT_COLOR} data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      className
    ) }
    { ...props }
  >
    <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2.5 w-2.5 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    { children }
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ ref }
    className={ cn(
      "px-3 py-2 text-2xl text-nfBlue-400 font-semibold text-center",
      font_title_secularOne.className,
      inset && "pr-8",
      className
    ) }
    { ...props }
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ ref }
    className={ cn(`-mx-1 my-2 h-px ${SEPARATOR_COLOR}`, className) }
    { ...props }
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={ cn(`mr-auto text-sm tracking-widest text-nfBlue-500`, className) }
      { ...props }
    />
  )
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
  DropdownMenuShortcut, DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger, DropdownMenuTrigger
}

